import { CatalogRecoveryTable } from "./catalog-recovery-table";
import { apiClient } from '@/api/api-client';
import MyContainer from '@/components/MyContainer';
import { BulkReceiveModal } from '@/components/products/BulkReceiveModal';
import { CategoryManagerModal } from '@/components/products/CategoryManagerModal';
import { ProductFilters } from '@/components/products/product-filters';
import StockClosingCard from '@/components/stock/StockClosingCard';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useBulkReceiveStock } from '@/hooks/product/use-bulk-receive-stock';
import { toast } from '@/hooks/use-toast';
import { useStockPage } from '@/pages/stock/hooks/useStockPage';
import { routes } from '@/routes/route-constants';
import { Stack } from '@mui/material';
import { AlertTriangle, Copy, ExternalLink, FolderTree, PackagePlus, PlusCircle, Send } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'wouter';
import { useProductContext } from '../context/product-context';
import { ProductTreeTable } from './product-tree-table';

export function ProductPageContent() {
  const { t } = useTranslation(['product', 'stock', 'common']);
  const [showBulkReceiveModal, setShowBulkReceiveModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showTelegramModal, setShowTelegramModal] = useState(false);
  const [miniAppUrl, setMiniAppUrl] = useState(() => `${window.location.origin}/pos`);
  const [chatId, setChatId] = useState('');
  const [isSendingTelegram, setIsSendingTelegram] = useState(false);

  const [, setLocation] = useLocation();
  const { handleRecomputeAllStock } = useStockPage();
  const { variants, isLoading, error, refresh, pagination, page, setPage, pageSize, setPageSize, includeDeleted, setIncludeDeleted, searchQuery, setSearchQuery } =
    useProductContext();

  const { handleBulkReceiveStock } = useBulkReceiveStock();

  const handleAddProduct = () => {
    setLocation(routes.product.new());
  };

  const handleCopyMiniAppUrl = () => {
    navigator.clipboard.writeText(miniAppUrl);
    toast({
      title: 'Copied',
      description: 'Telegram Mini App URL copied to clipboard.',
    });
  };

  const handleSendMiniAppToBot = async () => {
    setIsSendingTelegram(true);
    try {
      const response = await apiClient.post<any, { miniAppUrl: string; chatId?: string }>(
        '/telegram/mini-app/send',
        {
          miniAppUrl,
          chatId: chatId.trim() || undefined,
        }
      );
      toast({
        title: 'Sent to Telegram Bot',
        description:
          response?.data?.message || 'Mini App link was sent to your Telegram bot chat.',
      });
      setShowTelegramModal(false);
    } catch (err: any) {
      toast({
        title: 'Failed to send to Telegram',
        description: err?.message || 'Could not send message via Telegram bot.',
        variant: 'destructive',
      });
    } finally {
      setIsSendingTelegram(false);
    }
  };

  if (error) {
    return (
      <MyContainer
        title="Products"
        enableBack={false}
        subtitle="Manage your product catalog across all stores."
      >
        <div className="space-y-6 p-4">
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <div className="text-center">
                <AlertTriangle className="h-12 w-12 text-red-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-red-600">Error loading products</h3>
                <p className="text-muted-foreground">
                  {error.message || 'Failed to load products. Please try again.'}
                </p>
                <Button className="mt-4" onClick={() => window.location.reload()}>
                  Retry
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </MyContainer>
    );
  }

  return (
    <MyContainer
      title={t('product:title')}
      enableBack={false}
      subtitle={t('product:createDescription', { defaultValue: 'Manage your product catalog across all stores.' })}
      actions={
        <>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowCategoryModal(true)}
          >
            <FolderTree className="mr-2 h-4 w-4 text-indigo-600" />
            <span>Categories</span>
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="border-sky-200 text-sky-700 hover:bg-sky-50"
            onClick={() => setShowTelegramModal(true)}
          >
            <Send className="mr-2 h-4 w-4 text-sky-600" />
            <span>Telegram Mini App</span>
          </Button>
          <Button
            size="sm"
            onClick={() => {
              setShowBulkReceiveModal(true);
            }}
            variant="outline"
          >
            <PackagePlus className="mr-2 h-4 w-4" />
            <span>{t('stock:receiveStock')}</span>
          </Button>
          <Button onClick={handleAddProduct} size="sm">
            <PlusCircle className="mr-2 h-4 w-4" />
            {t('product:createProduct')}
          </Button>
        </>
      }
    >
      <div className="w-full max-w-full overflow-x-hidden">
        <Stack spacing={2} sx={{ width: '100%', maxWidth: '100%' }}>
          <StockClosingCard onRunClosing={handleRecomputeAllStock} />

          {/* Filters */}
          <ProductFilters />

          {/* Tree Table */}
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={includeDeleted} onChange={(event) => { setIncludeDeleted(event.target.checked); setPage(0); }} />{t("product:recovery.showDeleted")}</label>
          {includeDeleted ? <CatalogRecoveryTable items={variants} isLoading={isLoading} searchQuery={searchQuery} onSearch={(value) => { setSearchQuery(value); setPage(0); }} page={page} totalPages={pagination.totalPages} setPage={setPage} onRefresh={refresh} /> : <ProductTreeTable
            variants={variants}
            isLoading={isLoading}
            pagination={pagination}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            onRefresh={refresh}
          />}

          {/* Bulk Receive Modal */}
          <BulkReceiveModal
            isOpen={showBulkReceiveModal}
            productId={null}
            onSubmit={handleBulkReceiveStock}
            onCancel={() => setShowBulkReceiveModal(false)}
          />

          {/* Category Manager Modal */}
          <CategoryManagerModal
            open={showCategoryModal}
            onOpenChange={setShowCategoryModal}
          />

          {/* Telegram Mini App Modal */}
          <Dialog open={showTelegramModal} onOpenChange={setShowTelegramModal}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Send className="h-5 w-5 text-sky-600" />
                  Telegram Mini App Storefront
                </DialogTitle>
                <DialogDescription>
                  Products marked with <strong>Telegram Mini App</strong> are displayed publicly on your Mini App store. Orders placed there are created with source <strong>telegram</strong>.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-2">
                <div className="space-y-1.5">
                  <Label>Default Product Display Mode</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        try {
                          const url = new URL(miniAppUrl, window.location.origin);
                          url.searchParams.delete('view');
                          setMiniAppUrl(url.toString());
                        } catch {
                          setMiniAppUrl(`${window.location.origin}/pos`);
                        }
                      }}
                      className={`rounded-lg border p-2.5 text-left text-xs transition-all ${
                        !miniAppUrl.includes('view=variants')
                          ? 'border-sky-600 bg-sky-50/70 text-sky-900 ring-1 ring-sky-600'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-semibold">Grouped + Inline Pills</div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        1 card per product with quick variant chips
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        try {
                          const url = new URL(miniAppUrl, window.location.origin);
                          url.searchParams.set('view', 'variants');
                          setMiniAppUrl(url.toString());
                        } catch {
                          setMiniAppUrl(`${window.location.origin}/pos?view=variants`);
                        }
                      }}
                      className={`rounded-lg border p-2.5 text-left text-xs transition-all ${
                        miniAppUrl.includes('view=variants')
                          ? 'border-sky-600 bg-sky-50/70 text-sky-900 ring-1 ring-sky-600'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-semibold">All Variants Directly</div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        Show every variant as its own card (FB Live style)
                      </div>
                    </button>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="mini-app-url">Mini App Storefront URL</Label>
                  <div className="flex gap-2">
                    <Input
                      id="mini-app-url"
                      value={miniAppUrl}
                      onChange={(e) => setMiniAppUrl(e.target.value)}
                      placeholder="https://your-domain.com/pos"
                    />
                    <Button type="button" variant="outline" size="icon" onClick={handleCopyMiniAppUrl} title="Copy URL">
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => window.open(miniAppUrl, '_blank')}
                      title="Open Storefront Preview"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Tip: For Telegram WebApp button in BotFather or inline button, use an HTTPS URL (or localhost/ngrok when testing).
                  </p>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="telegram-chat-id">Telegram Chat ID (Optional)</Label>
                  <Input
                    id="telegram-chat-id"
                    value={chatId}
                    onChange={(e) => setChatId(e.target.value)}
                    placeholder="Leave empty to use default configured bot chat ID"
                  />
                </div>
              </div>

              <DialogFooter className="flex sm:justify-between gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => window.open(miniAppUrl, '_blank')}
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Preview Store
                </Button>
                <Button
                  type="button"
                  onClick={handleSendMiniAppToBot}
                  disabled={isSendingTelegram}
                  className="bg-sky-600 hover:bg-sky-700 text-white"
                >
                  <Send className="mr-2 h-4 w-4" />
                  {isSendingTelegram ? 'Sending...' : 'Send to Telegram Bot'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </Stack>
      </div>
    </MyContainer>
  );
}
